import * as CommonSelectors from "./lib/common_selectors";
import isEventAtCoordinates from "./lib/is_event_at_coordinates";
import doubleClickZoom from "./lib/double_click_zoom";
import * as Constants from "./constants";
import createVertex from "./lib/create_vertex";

const CustomDrawLineString = {};

CustomDrawLineString.onSetup = function (opts) {
  opts = opts || {};
  const { featureId, from } = opts;

  let line;
  let currentVertexPosition;
  let direction = "forward";
  if (featureId) {
    line = this.getFeature(featureId);
    if (!line) {
      throw new Error("Could not find a feature with the provided featureId");
    }
    if (
      from &&
      from.type === "Point" &&
      from.coordinates &&
      from.coordinates.length === 2
    ) {
      const lastCoord = line.coordinates.length - 1;
      if (
        line.coordinates[lastCoord][0] === from.coordinates[0] &&
        line.coordinates[lastCoord][1] === from.coordinates[1]
      ) {
        currentVertexPosition = lastCoord + 1;
        // add one new coordinate to continue from
        line.addCoordinate(
          currentVertexPosition,
          ...line.coordinates[lastCoord]
        );
      } else if (
        line.coordinates[0][0] === from.coordinates[0] &&
        line.coordinates[0][1] === from.coordinates[1]
      ) {
        direction = "backwards";
        currentVertexPosition = 0;
        // add one new coordinate to continue from
        line.addCoordinate(currentVertexPosition, ...line.coordinates[0]);
      } else {
        throw new Error(
          "`from` should match the point at either the start or the end of the provided LineString"
        );
      }
    } else {
      throw new Error(
        "Please use the `from` property to indicate which point to continue the line from"
      );
    }
  } else {
    line = this.newFeature({
      type: Constants.geojsonTypes.FEATURE,
      properties: {},
      geometry: {
        type: Constants.geojsonTypes.LINE_STRING,
        coordinates: [],
      },
    });
    currentVertexPosition = 0;
    this.addFeature(line);
  }

  this.clearSelectedFeatures();
  doubleClickZoom.disable(this);
  this.updateUIClasses({ mouse: Constants.cursors.ADD });
  this.activateUIButton(Constants.types.LINE);
  this.setActionableState({
    trash: true,
  });

  const sourceId = "lastDrawingSegment";
  const layerId = "lastDrawingSegment";

  const source = this.map.getSource(sourceId);
  if (!source) {
    this.map.addSource(sourceId, {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [],
      },
    });
  }

  const layer = this.map.getLayer(layerId);
  if (!layer) {
    this.map.addLayer({
      id: layerId,
      source: sourceId,
      type: "line",
      paint: {
        "line-color": "#FFFFFF",
        "line-width": 3,
        "line-dasharray": [1, 1],
      },
    });

    // Set To Highest zIndex
    const layerIds = this.map.getStyle().layers.map((layer) => layer.id);
    const highestIndex = layerIds.length - 1;
    const topLayerId = layerIds[highestIndex];
    this.map.moveLayer(layerId, topLayerId);
  }

  return {
    line,
    currentVertexPosition,
    direction,
  };
};

CustomDrawLineString.clickAnywhere = function (state, e) {
  if (
    (state.currentVertexPosition > 0 &&
      isEventAtCoordinates(
        e,
        state.line.coordinates[state.currentVertexPosition - 1]
      )) ||
    (state.direction === "backwards" &&
      isEventAtCoordinates(
        e,
        state.line.coordinates[state.currentVertexPosition + 1]
      ))
  ) {
    return this.changeMode(Constants.modes.SIMPLE_SELECT, {
      featureIds: [state.line.id],
    });
  }
  this.updateUIClasses({ mouse: Constants.cursors.ADD });
  state.line.updateCoordinate(
    state.currentVertexPosition,
    e.lngLat.lng,
    e.lngLat.lat
  );
  if (state.direction === "forward") {
    state.currentVertexPosition++;
    state.line.updateCoordinate(
      state.currentVertexPosition,
      e.lngLat.lng,
      e.lngLat.lat
    );
  } else {
    state.line.addCoordinate(0, e.lngLat.lng, e.lngLat.lat);
  }
};

CustomDrawLineString.clickOnVertex = function (state) {
  this.map.fire("draw.onLineStop", {
    state,
  });
  return this.changeMode(Constants.modes.SIMPLE_SELECT, {
    featureIds: [state.line.id],
  });
};

// CustomDrawLineString.layerId = "lastDrawingSegment"; // Replace with the ID of your line layer

CustomDrawLineString.onMouseMove = function (state, e) {
  state.line.updateCoordinate(
    state.currentVertexPosition,
    e.lngLat.lng,
    e.lngLat.lat
  );
  if (CommonSelectors.isVertex(e)) {
    this.updateUIClasses({ mouse: Constants.cursors.POINTER });
  }
  // Get the last drawn vertex
  const lastVertex = state.line.coordinates[state.line.coordinates.length - 2];

  // Slice the line between the last vertex and the current mouse cursor
  const slicedCoordinates = [];
  if (lastVertex) {
    // Add the last drawn vertex
    slicedCoordinates.push(lastVertex);
    // Add the current mouse cursor position
    slicedCoordinates.push([e.lngLat.lng, e.lngLat.lat]);
  }

  // Create a GeoJSON LineString feature with the sliced coordinates
  const slicedLine = {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: slicedCoordinates,
    },
    properties: {}, // You can add properties if needed
  };

  // Set the layer and data
  const data = {
    type: "FeatureCollection",
    features: [slicedLine],
  };

  // Update the layer with the new data
  this.map.getSource("lastDrawingSegment").setData(data);
};

CustomDrawLineString.onTap = CustomDrawLineString.onClick = function (
  state,
  e
) {
  this.map.fire("draw.onLineTap", {
    e,
    state,
    featureId: state.line.id,
  });

  if (CommonSelectors.isVertex(e)) return this.clickOnVertex(state, e);
  this.clickAnywhere(state, e);
};

CustomDrawLineString.onKeyUp = function (state, e) {
  console.log("CustomDrawLineString.onKeyUp", CommonSelectors);
  if (CommonSelectors.isEscapeKey(e)){
    console.log("Escape key pressed");

    this.deleteFeature([state.line.id], { silent: true });
    this.changeMode(Constants.modes.SIMPLE_SELECT);
    setTimeout(() => {
      this.changeMode(Constants.modes.CUSTOM_DRAW_LINE_STRING);
    }, 100);
  } else if (CommonSelectors.isBackSpaceKey(e)) {

  // ON TRASH
  } else if (CommonSelectors.isSpaceKey(e)) {
    this.addCoordinateAtCurrentPosition(state);

   
  }  else if (CommonSelectors.isEnterKey(e)) {

    this.changeMode(Constants.modes.SIMPLE_SELECT, {
      featureIds: [state.line.id],
    });
    setTimeout(() => {
      this.changeMode(Constants.modes.CUSTOM_DRAW_LINE_STRING);
    }, 100);
  } else if (e.key === "q") {
    // Finalize the line drawing and switch to simple select mode
    this.map.fire(Constants.events.CREATE, {
      features: [state.line.toGeoJSON()],
    });
    return this.changeMode(Constants.modes.SIMPLE_SELECT, {
      featureIds: [state.line.id],
    });
  }
};

CustomDrawLineString.addCoordinateAtCurrentPosition = function (state) {
  const center = this.map.getCenter();
  state.line.updateCoordinate(
    state.currentVertexPosition,
    center.lng,
    center.lat
  );
  state.currentVertexPosition++;
  state.line.addCoordinate(state.currentVertexPosition, center.lng, center.lat);
};

CustomDrawLineString.onStop = function (state) {
  doubleClickZoom.enable(this);
  this.activateUIButton();
  const source = this.map.getSource("lastDrawingSegment");
  if (source) {
    source.setData({
      type: "FeatureCollection",
      features: [],
    });
  }

  // check to see if we've deleted this feature
  if (this.getFeature(state.line.id) === undefined) return;

  // remove last added coordinate
  state.line.removeCoordinate(`${state.currentVertexPosition}`);
  if (state.line.isValid()) {
    this.map.fire(Constants.events.CREATE, {
      features: [state.line.toGeoJSON()],
    });
  } else {
    this.deleteFeature([state.line.id], { silent: true });
    this.changeMode(Constants.modes.SIMPLE_SELECT, {}, { silent: true });
  }
};

CustomDrawLineString.onTrash = function (state) {
  console.log("Trash custom draw line string");
  console.log("Backspace key pressed");

  const { coordinates } = state.line;
  const numCoordinates = coordinates.length;

  if (numCoordinates > 1) {
    // Remove the last coordinate
    coordinates.pop();

    // Update the last coordinate index
    state.currentVertexPosition = numCoordinates - 2;

    // Update the UI classes and selected features
    this.updateUIClasses({ mouse: Constants.cursors.ADD });
    this.clearSelectedFeatures();
    
    // Update the line coordinates directly instead of using updateCoordinate
    state.line.updateCoordinate(state.currentVertexPosition, coordinates[coordinates.length-1][0], coordinates[coordinates.length-1][1]);

    // Activate the default button and set actionable state
    this.activateUIButton();
    this.setActionableState({
      trash: numCoordinates > 2,
    });
  }

  console.log("Trash custom draw line string");
};

CustomDrawLineString.toDisplayFeatures = function (state, geojson, display) {
  const isActiveLine = geojson.properties.id === state.line.id;
  geojson.properties.active = isActiveLine
    ? Constants.activeStates.ACTIVE
    : Constants.activeStates.INACTIVE;
  if (!isActiveLine) return display(geojson);
  // Only render the line if it has at least one real coordinate
  if (geojson.geometry.coordinates.length < 2) return;
  geojson.properties.meta = Constants.meta.FEATURE;
  display(
    createVertex(
      state.line.id,
      geojson.geometry.coordinates[
        state.direction === "forward"
          ? geojson.geometry.coordinates.length - 2
          : 1
      ],
      `${
        state.direction === "forward"
          ? geojson.geometry.coordinates.length - 2
          : 1
      }`,
      false
    )
  );

  display(geojson);
};

export default CustomDrawLineString;
